import React, { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Component } from '../../../../core/types/gameobject.types';
import {
  CascadeSlotConfig,
  CascadeSymbolConfig,
  MultiplierPoolEntry,
  FreeSpinsConfig,
  DEFAULT_CASCADE_SLOT_CONFIG,
} from '../../../../core/types/cascade-slot.types';
import { useSceneStore } from '../../../store/sceneStore';
import { NumberInput } from './NumberInput';
import { SymbolListEditor } from './SymbolListEditor';
import { PaytableBracketEditor } from './PaytableBracketEditor';
import { MultiplierPoolEditor } from './MultiplierPoolEditor';
import { BetLevelsEditor } from './BetLevelsEditor';
import { cn } from '../../../utils/cn';

interface CascadeSlotInspectorProps {
  objectId: string;
  component: Component;
}

type SectionId = 'grid' | 'symbols' | 'paytable' | 'cascade' | 'multiplier' | 'freeSpins' | 'economy' | 'animation';

function getData<T>(data: Record<string, unknown>, key: string): T {
  return (data[key] ?? (DEFAULT_CASCADE_SLOT_CONFIG as Record<string, unknown>)[key]) as T;
}

const Section: React.FC<{
  title: string;
  id: SectionId;
  expanded: Set<SectionId>;
  toggle: (id: SectionId) => void;
  children: React.ReactNode;
}> = ({ title, id, expanded, toggle, children }) => {
  const isOpen = expanded.has(id);
  return (
    <div className="border-b border-zinc-700 last:border-b-0">
      <button
        className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs font-semibold text-zinc-300 hover:bg-zinc-700/50"
        onClick={() => toggle(id)}
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {title}
      </button>
      {isOpen && <div className="px-2 pb-2 space-y-2">{children}</div>}
    </div>
  );
};

const LabeledRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs text-zinc-500 whitespace-nowrap">{label}</span>
    <div className="flex-1 max-w-[140px]">{children}</div>
  </div>
);

export const CascadeSlotInspector: React.FC<CascadeSlotInspectorProps> = ({
  objectId,
  component,
}) => {
  const updateComponent = useSceneStore((s) => s.updateComponent);

  const [expanded, setExpanded] = useState<Set<SectionId>>(
    new Set(['grid', 'symbols'])
  );

  const toggle = useCallback((id: SectionId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const update = useCallback(
    (key: string, value: unknown) => {
      updateComponent(objectId, component.id, { [key]: value });
    },
    [objectId, component.id, updateComponent]
  );

  const { data } = component;

  return (
    <div className="space-y-0">
      {/* Grid */}
      <Section title="Grid" id="grid" expanded={expanded} toggle={toggle}>
        <LabeledRow label="Rows">
          <NumberInput value={getData<number>(data, 'rows')} min={3} max={10} step={1} onChange={(v) => update('rows', v)} />
        </LabeledRow>
        <LabeledRow label="Columns">
          <NumberInput value={getData<number>(data, 'columns')} min={3} max={8} step={1} onChange={(v) => update('columns', v)} />
        </LabeledRow>
        <LabeledRow label="Symbol W">
          <NumberInput value={getData<number>(data, 'symbolWidth')} min={50} max={300} step={1} onChange={(v) => update('symbolWidth', v)} />
        </LabeledRow>
        <LabeledRow label="Symbol H">
          <NumberInput value={getData<number>(data, 'symbolHeight')} min={50} max={300} step={1} onChange={(v) => update('symbolHeight', v)} />
        </LabeledRow>
        <LabeledRow label="Spacing">
          <NumberInput value={getData<number>(data, 'symbolSpacing')} min={0} max={20} step={1} onChange={(v) => update('symbolSpacing', v)} />
        </LabeledRow>
      </Section>

      {/* Symbols */}
      <Section title="Symbols" id="symbols" expanded={expanded} toggle={toggle}>
        <SymbolListEditor
          symbols={getData<CascadeSymbolConfig[]>(data, 'symbols')}
          onChange={(symbols) => update('symbols', symbols)}
        />
      </Section>

      {/* Paytable */}
      <Section title="Win Detection" id="paytable" expanded={expanded} toggle={toggle}>
        <LabeledRow label="Min Match">
          <NumberInput value={getData<number>(data, 'minMatchCount')} min={4} max={12} step={1} onChange={(v) => update('minMatchCount', v)} />
        </LabeledRow>
        <div className="text-xs text-zinc-400 font-semibold mt-1 mb-0.5">Paytable</div>
        <PaytableBracketEditor
          symbols={getData<CascadeSymbolConfig[]>(data, 'symbols')}
          countBrackets={getData<number[]>(data, 'countBrackets')}
          onChange={(symbols) => update('symbols', symbols)}
        />
      </Section>

      {/* Cascade */}
      <Section title="Cascade" id="cascade" expanded={expanded} toggle={toggle}>
        <LabeledRow label="Enabled">
          <input
            type="checkbox"
            checked={getData<boolean>(data, 'cascadeEnabled')}
            onChange={(e) => update('cascadeEnabled', e.target.checked)}
            className="accent-blue-500"
          />
        </LabeledRow>
        <LabeledRow label="Direction">
          <select
            className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 w-full"
            value={getData<string>(data, 'cascadeDirection')}
            onChange={(e) => update('cascadeDirection', e.target.value)}
          >
            <option value="vertical">Vertical</option>
            <option value="horizontal">Horizontal</option>
          </select>
        </LabeledRow>
        <LabeledRow label="Anim Speed">
          <NumberInput value={getData<number>(data, 'cascadeAnimSpeed')} min={100} max={1000} step={50} onChange={(v) => update('cascadeAnimSpeed', v)} />
        </LabeledRow>
      </Section>

      {/* Multiplier Pool */}
      <Section title="Multiplier Pool" id="multiplier" expanded={expanded} toggle={toggle}>
        <MultiplierPoolEditor
          pool={getData<MultiplierPoolEntry[]>(data, 'multiplierPool')}
          onChange={(pool) => update('multiplierPool', pool)}
        />
      </Section>

      {/* Free Spins */}
      <Section title="Free Spins" id="freeSpins" expanded={expanded} toggle={toggle}>
        <LabeledRow label="Trigger Count">
          <NumberInput value={getData<FreeSpinsConfig>(data, 'freeSpins').triggerCount} min={2} max={6} step={1} onChange={(v) => {
            const fs = { ...getData<FreeSpinsConfig>(data, 'freeSpins'), triggerCount: v };
            update('freeSpins', fs);
          }} />
        </LabeledRow>
        <LabeledRow label="Spins Awarded">
          <NumberInput value={getData<FreeSpinsConfig>(data, 'freeSpins').spinsAwarded} min={1} max={50} step={1} onChange={(v) => {
            const fs = { ...getData<FreeSpinsConfig>(data, 'freeSpins'), spinsAwarded: v };
            update('freeSpins', fs);
          }} />
        </LabeledRow>
        <LabeledRow label="Retrigger Count">
          <NumberInput value={getData<FreeSpinsConfig>(data, 'freeSpins').retriggerCount} min={1} max={6} step={1} onChange={(v) => {
            const fs = { ...getData<FreeSpinsConfig>(data, 'freeSpins'), retriggerCount: v };
            update('freeSpins', fs);
          }} />
        </LabeledRow>
        <LabeledRow label="Retrigger Spins">
          <NumberInput value={getData<FreeSpinsConfig>(data, 'freeSpins').retriggerSpinsAwarded} min={1} max={20} step={1} onChange={(v) => {
            const fs = { ...getData<FreeSpinsConfig>(data, 'freeSpins'), retriggerSpinsAwarded: v };
            update('freeSpins', fs);
          }} />
        </LabeledRow>
      </Section>

      {/* Bet & Economy */}
      <Section title="Bet & Economy" id="economy" expanded={expanded} toggle={toggle}>
        <div className="text-xs text-zinc-400 font-semibold mb-0.5">Bet Levels</div>
        <BetLevelsEditor
          betLevels={getData<number[]>(data, 'betLevels')}
          onChange={(betLevels) => update('betLevels', betLevels)}
        />
        <LabeledRow label="Starting Balance">
          <NumberInput value={getData<number>(data, 'startingBalance')} min={100} max={1000000} step={100} onChange={(v) => update('startingBalance', v)} />
        </LabeledRow>
        <LabeledRow label="Buy Feature">
          <input
            type="checkbox"
            checked={getData<boolean>(data, 'buyFeatureEnabled')}
            onChange={(e) => update('buyFeatureEnabled', e.target.checked)}
            className="accent-blue-500"
          />
        </LabeledRow>
        <LabeledRow label="Buy Cost (xBet)">
          <NumberInput value={getData<number>(data, 'buyFeatureCostMultiplier')} min={10} max={500} step={10} onChange={(v) => update('buyFeatureCostMultiplier', v)} />
        </LabeledRow>
        <LabeledRow label="Max Win (xBet)">
          <NumberInput value={getData<number>(data, 'maxWinMultiplier')} min={1000} max={100000} step={1000} onChange={(v) => update('maxWinMultiplier', v)} />
        </LabeledRow>
      </Section>

      {/* Animation */}
      <Section title="Animation" id="animation" expanded={expanded} toggle={toggle}>
        <LabeledRow label="Spin Duration">
          <NumberInput value={getData<number>(data, 'spinDuration')} min={0.1} max={3} step={0.1} onChange={(v) => update('spinDuration', v)} />
        </LabeledRow>
        <LabeledRow label="Turbo Multi">
          <NumberInput value={getData<number>(data, 'turboMultiplier')} min={1} max={10} step={1} onChange={(v) => update('turboMultiplier', v)} />
        </LabeledRow>
        <LabeledRow label="Win Present">
          <NumberInput value={getData<number>(data, 'winPresentDuration')} min={0.1} max={3} step={0.1} onChange={(v) => update('winPresentDuration', v)} />
        </LabeledRow>
        <LabeledRow label="Remove Dur">
          <NumberInput value={getData<number>(data, 'cascadeRemoveDuration')} min={0.1} max={2} step={0.1} onChange={(v) => update('cascadeRemoveDuration', v)} />
        </LabeledRow>
        <LabeledRow label="Fill Dur">
          <NumberInput value={getData<number>(data, 'cascadeFillDuration')} min={0.1} max={2} step={0.1} onChange={(v) => update('cascadeFillDuration', v)} />
        </LabeledRow>
        <LabeledRow label="Gravity Dur">
          <NumberInput value={getData<number>(data, 'cascadeGravityDuration')} min={0.05} max={2} step={0.05} onChange={(v) => update('cascadeGravityDuration', v)} />
        </LabeledRow>
        <LabeledRow label="Column Delay">
          <NumberInput value={getData<number>(data, 'cascadeColumnDelay')} min={0} max={100} step={5} onChange={(v) => update('cascadeColumnDelay', v)} />
        </LabeledRow>
        <LabeledRow label="Win Scale">
          <NumberInput value={getData<number>(data, 'winHighlightScale')} min={1.0} max={1.3} step={0.05} onChange={(v) => update('winHighlightScale', v)} />
        </LabeledRow>
        <LabeledRow label="Fall Style">
          <select
            className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-xs text-zinc-300 w-full"
            value={getData<string>(data, 'cascadeFallStyle')}
            onChange={(e) => update('cascadeFallStyle', e.target.value)}
          >
            <option value="bounce">Bounce</option>
            <option value="smooth">Smooth</option>
            <option value="elastic">Elastic</option>
            <option value="drop">Drop</option>
            <option value="fade">Fade</option>
          </select>
        </LabeledRow>
      </Section>
    </div>
  );
};
